// @ts-nocheck
// "Fast answers" — the UI over laya_config. The word "classifier" never
// appears on screen; the user sees questions and the replies to them.
import { useState } from 'react';
import { AlertTriangle, Pause, Play, Plus, Trash2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { cn } from '@/lib/utils/cn';
import { fieldTone } from '@/lib/utils/fieldTone';
import { newLayaAnswer, overlappingAnswers, toSnakeCase, validateLaya } from './layaLlm';

const EXAMPLE = {
  description: 'asks about check-in or check-out time',
  answer: 'Check-in is from 2 PM and check-out is until 11 AM.',
};

export default function LayaSection({ value, onChange, globallyDisabled, readOnly }) {
  const [showTechnical, setShowTechnical] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const { errors, warnings } = validateLaya(value);
  const overlaps = overlappingAnswers(value.answers);

  const patch = (next) => onChange({ ...value, ...next });
  const patchAnswer = (id, next) =>
    onChange({ ...value, answers: value.answers.map((a) => (a.id === id ? { ...a, ...next } : a)) });

  const addAnswer = (seed = {}) => {
    onChange({ ...value, enabled: true, answers: [...value.answers, { ...newLayaAnswer(), ...seed }] });
    toast.success('Fast answer added');
  };

  // green when complete, yellow when worth a look, red when it blocks publishing
  const descTone = (a) =>
    a.answer?.trim() && !a.description?.trim() ? 'error'
      : overlaps[a.id] ? 'warn'
      : a.description?.trim() ? 'ok' : 'idle';
  const answerTone = (a) =>
    /[<>]/.test(a.answer || '') || (a.description?.trim() && !a.answer?.trim()) ? 'error'
      : (a.answer || '').length > 240 ? 'warn'
      : a.answer?.trim() ? 'ok' : 'idle';

  return (
    <div className="space-y-5">
      {globallyDisabled && (
        <p className="rounded-xl border border-border bg-muted/60 px-4 py-3 text-sm text-muted-foreground">
          Fast answers are turned off for all agents by your admin. Your settings here are saved and
          will apply once it is switched back on.
        </p>
      )}

      {/* on/off — pausing keeps every answer */}
      <div className="flex items-start justify-between gap-4 rounded-xl border border-border bg-card/70 px-4 py-3">
        <div className="min-w-0">
          <Label htmlFor="laya-enabled" className="text-sm font-semibold">
            Fast answers
          </Label>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            Common questions answered instantly, without waiting for the AI. Leave it off if you are
            not sure.
          </p>
        </div>
        <Switch
          id="laya-enabled"
          checked={!!value.enabled}
          disabled={readOnly}
          onCheckedChange={(enabled) => {
            patch({ enabled });
            toast.success(enabled ? 'Fast answers turned on' : 'Fast answers paused');
          }}
        />
      </div>

      {!value.enabled ? (
        value.answers.length > 0 ? (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3">
            <p className="text-sm text-muted-foreground">
              Paused — calls go straight to the AI. Your {value.answers.length} answer
              {value.answers.length === 1 ? '' : 's'} are kept.
            </p>
            <Button size="sm" variant="outline" disabled={readOnly} onClick={() => patch({ enabled: true })}>
              <Play className="h-3.5 w-3.5" /> Resume
            </Button>
          </div>
        ) : null
      ) : (
        <>
          {errors.map((e) => (
            <p key={e} className="flex gap-2 rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-2.5 text-sm text-destructive">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {e}
            </p>
          ))}
          {warnings.map((w) => (
            <p key={w} className="rounded-xl border border-border bg-muted/50 px-4 py-2.5 text-sm text-muted-foreground">
              {w}
            </p>
          ))}

          {value.answers.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-card/50 px-6 py-8 text-center">
              <p className="text-sm font-medium text-foreground">No fast answers yet</p>
              <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
                Pick the questions callers ask every day and write the reply once. Everything else
                still goes to the AI.
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                <Button size="sm" disabled={readOnly} onClick={() => addAnswer()}>
                  <Plus className="h-4 w-4" /> Add answer
                </Button>
                <Button size="sm" variant="outline" disabled={readOnly} onClick={() => addAnswer(EXAMPLE)}>
                  <Sparkles className="h-4 w-4" /> Insert an example
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {value.answers.map((a, i) => (
                <div
                  key={a.id || i}
                  className={cn(
                    'rounded-xl border border-border bg-card/70 p-4 shadow-sm transition-opacity',
                    a.enabled === false && 'opacity-55',
                  )}
                >
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Answer {i + 1}
                    </span>
                    <div className="flex items-center gap-1">
                      {a.enabled === false && <Badge variant="secondary">paused</Badge>}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        disabled={readOnly}
                        aria-label={a.enabled === false ? 'Resume this answer' : 'Pause this answer'}
                        onClick={() => {
                          patchAnswer(a.id, { enabled: a.enabled === false });
                          toast.success(a.enabled === false ? 'Answer resumed' : 'Answer paused');
                        }}
                      >
                        {a.enabled === false ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        disabled={readOnly}
                        aria-label="Delete this answer"
                        onClick={() => {
                          onChange({ ...value, answers: value.answers.filter((x) => x.id !== a.id) });
                          toast.success('Fast answer removed');
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label className="text-xs">When the caller asks…</Label>
                      <Input
                        className={fieldTone(descTone(a))}
                        value={a.description}
                        disabled={readOnly}
                        placeholder="asks about parking"
                        onChange={(e) =>
                          patchAnswer(a.id, {
                            description: e.target.value,
                            label: a.labelEdited ? a.label : toSnakeCase(e.target.value),
                          })
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Say this…</Label>
                      <Textarea
                        rows={2}
                        className={fieldTone(answerTone(a))}
                        value={a.answer}
                        disabled={readOnly}
                        placeholder="Parking is free for guests, in the basement."
                        onChange={(e) => patchAnswer(a.id, { answer: e.target.value })}
                      />
                    </div>
                  </div>

                  {overlaps[a.id] && (
                    <p className="mt-3 flex gap-2 rounded-lg border border-warning/50 bg-warning/10 px-3 py-2 text-xs text-foreground">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
                      <span>
                        This sounds like "{overlaps[a.id]}". Callers may get the wrong reply. Make the two lines
                        clearly different, or merge them into one answer.
                      </span>
                    </p>
                  )}

                  {showTechnical && (
                    <div className="mt-3 space-y-1.5">
                      <Label className="text-xs">Technical name</Label>
                      <Input
                        value={a.label}
                        disabled={readOnly}
                        onChange={(e) => patchAnswer(a.id, { label: e.target.value, labelEdited: true })}
                      />
                    </div>
                  )}
                </div>
              ))}

              {/* the `other` catch-all is still saved (see layaLlm.js), just not shown */}
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" disabled={readOnly} onClick={() => addAnswer()}>
                  <Plus className="h-4 w-4" /> Add answer
                </Button>
                <button
                  type="button"
                  className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                  onClick={() => setShowTechnical((v) => !v)}
                >
                  {showTechnical ? 'Hide technical names' : 'Show technical names'}
                </button>
              </div>
            </div>
          )}

          {/* Try it — needs a backend endpoint that does not exist yet */}
          <div className="rounded-xl border border-border bg-card/50 p-4 opacity-70">
            <div className="flex items-center gap-2">
              <Label className="text-sm">Try it</Label>
              <Badge variant="secondary">coming soon</Badge>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Type a caller question and see which answer would be picked.
            </p>
            <Input className="mt-3" disabled placeholder="What time can I check in?" />
          </div>

          <div>
            <button
              type="button"
              className="text-xs text-muted-foreground underline-offset-2 hover:underline"
              onClick={() => setShowAdvanced((v) => !v)}
            >
              {showAdvanced ? 'Hide advanced' : 'Advanced'}
            </button>
            {showAdvanced && (
              <div className="mt-3 grid gap-3 rounded-xl border border-border bg-card/60 p-4 md:grid-cols-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Mode</Label>
                  <Input value={value.mode} disabled={readOnly} onChange={(e) => patch({ mode: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Context turns</Label>
                  <Input
                    type="number"
                    min={0}
                    value={value.contextTurns}
                    disabled={readOnly}
                    onChange={(e) => patch({ contextTurns: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Language</Label>
                  <Input value={value.lang} disabled={readOnly} onChange={(e) => patch({ lang: e.target.value })} />
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
